import { InvoicePaymentStatus, InvoiceStatus, TransactionType } from "../enums";
import {
  CountryCode,
  DropdownItem,
  InvoiceStatusTargetActions,
} from "../types";
import { US_STATES, CANADA_PROVINCES } from "./states";

export const ACCOUNT_DETAILS_TAB_KEY = "tab";
export const ORGANIZATION_DETAILS_TAB_KEY = "tab";
export const SESSION_STORAGE_USER_KEY = "user";
export const SESSION_STORAGE_DP_USER_KEY = "dp-user";
export const INTUIT_CONNECT_TO_QUICKBOOKS_CALLBACK_SOURCE =
  "intuitQuickbooksOAuthCallback";

export const DATE_RANGE_OPTIONS = [
  "Today",
  "Yesterday",
  "This Week",
  "Last Week",
  "This Month",
  "Last Month",
  "This Quarter",
  "Last Quarter",
  "Last 6 Months",
  "Last 12 Months",
  "This Year",
  "Custom Date",
];

export const WASH_FREQUENCIES: DropdownItem[] = [
  { key: "Daily", value: "daily" },
  { key: "Weekly", value: "weekly" },
  { key: "Biweekly", value: "biweekly" },
  { key: "Monthly", value: "monthly" },
  { key: "Semi-Monthly", value: "semi-monthly" },
  { key: "Semi-Annual", value: "semi-annual" },
  { key: "Yearly", value: "yearly" },
  { key: "7 Days", value: "7days" },
  { key: "13 Days", value: "13days" },
  { key: "30 Days", value: "30days" },
  { key: "181 Days", value: "181days" },
  { key: "Custom", value: "custom" },
];

export const INVOICE_FREQUENCIES: DropdownItem[] = [
  { key: "Daily", value: "daily" },
  { key: "Weekly", value: "weekly" },
  { key: "Biweekly", value: "biweekly" },
  { key: "Monthly", value: "monthly" },
];

export const DUE_UPON: DropdownItem[] = [
  { key: "Immediate Payment", value: "0" },
  { key: "Net 7", value: "7" },
  { key: "Net 14", value: "14" },
  { key: "Net 30", value: "30" },
  { key: "Net 60", value: "60" },
  { key: "Custom", value: "custom" },
];

export const DEFAULT_QUERY_OPTIONS: any = {
  retry: false,
};

export const INVOICE_STATUS_TARGET_ACTIONS_ADMIN: InvoiceStatusTargetActions = {
  [InvoiceStatus.Pending]: {
    defaultAction: InvoiceStatus.SendToQuickbooks,
    targetActions: [InvoiceStatus.SendToCustomer, InvoiceStatus.Cancelled],
  },
  [InvoiceStatus.InProgress]: {
    defaultAction: InvoiceStatus.PaymentReceived,
    targetActions: [
      InvoiceStatus.SendToCustomer,
      InvoiceStatus.OnHold,
      InvoiceStatus.Pending,
      InvoiceStatus.Cancelled,
    ],
  },
  [InvoiceStatus.SendToCustomer]: {
    defaultAction: InvoiceStatus.PaymentReceived,
    targetActions: [
      InvoiceStatus.OnHold,
      InvoiceStatus.Pending,
      InvoiceStatus.Cancelled,
    ],
  },
  [InvoiceStatus.OnHold]: {
    defaultAction: InvoiceStatus.PaymentReceived,
    targetActions: [
      InvoiceStatus.Pending,
      InvoiceStatus.SendToCustomer,
      InvoiceStatus.Cancelled,
    ],
  },
  [InvoiceStatus.All]: {
    defaultAction: InvoiceStatus.SendToCustomer,
    targetActions: [
      InvoiceStatus.PaymentReceived,
      InvoiceStatus.OnHold,
      InvoiceStatus.Pending,
      InvoiceStatus.InProgress,
      InvoiceStatus.Cancelled,
    ],
  },
  [InvoiceStatus.Cancelled]: {
    defaultAction: InvoiceStatus.Deleted,
  },
  [InvoiceStatus.PaymentReceived]: {
    defaultAction: InvoiceStatus.InProgress,
  },
};

export const INVOICE_STATUS_TARGET_ACTIONS_CUSTOMER: InvoiceStatusTargetActions =
  {
    [InvoiceStatus.InProgress]: {
      defaultAction: InvoiceStatus.PaymentReceived,
    },
    [InvoiceStatus.SendToCustomer]: {
      defaultAction: InvoiceStatus.PaymentReceived,
    },
  };

export const CUSTOMER_INVOICE_STATUS_MAP: Record<string, InvoiceStatus[]> = {
  All: [
    InvoiceStatus.OnHold,
    InvoiceStatus.PaymentReceived,
    InvoiceStatus.InProgress,
    InvoiceStatus.SendToCustomer,
  ],
  Unpaid: [
    InvoiceStatus.OnHold,
    InvoiceStatus.InProgress,
    InvoiceStatus.SendToCustomer,
  ],
  Paid: [InvoiceStatus.PaymentReceived],
};

export const CUSTOMER_INVOICE_STATUS_FILTER_DROPDOWN: DropdownItem[] =
  Object.entries(CUSTOMER_INVOICE_STATUS_MAP).map(([key, values]) => ({
    key,
    value: values.join("|"),
  }));

export const CARD_LOGOS: { [key: string]: string } = {
  Visa: "/card-images/Visa.png",
  Mastercard: "/card-images/MasterCard.png",
  Amex: "/card-images/Amex.png",
  Discover: "/card-images/Discover.png",
  Diners: "/card-images/Diners.png",
  StoredValue: "/card-images/StoredValue.png",
  Other: "/card-images/Other.png",
  JCB: "/card-images/JCB.webp",
  CarteBlanche: "/card-images/CarteBlanche.png",
  Interac: "/card-images/Interac.png",
  UnionPay: "/card-images/UnionPay.png",
};

export const STATUS_LABELS: Record<string, string> = {
  [InvoiceStatus.All]: "All",
  [InvoiceStatus.Pending]: "Pending",
  [InvoiceStatus.SendToCustomer]: "Sent to Customer",
  [InvoiceStatus.SendToQuickbooks]: "Sent to Quickbooks",
  [InvoiceStatus.Cancelled]: "Cancelled",
  [InvoiceStatus.Deleted]: "Deleted",
  [InvoiceStatus.OnHold]: "On Hold",
  [InvoiceStatus.PaymentReceived]: "Payment Received",
  [InvoiceStatus.InProgress]: "In Progress",
};

export const PAYMENT_STATUS_LABELS: Record<InvoicePaymentStatus, string> = {
  [InvoicePaymentStatus.FullyPaid]: "Paid",
  [InvoicePaymentStatus.PartiallyPaid]: "Partial Payment",
  [InvoicePaymentStatus.Overdue]: "Past Due",
  [InvoicePaymentStatus.Unpaid]: "Not Paid",
};

export const PAYMENT_STATUS_COLORS: Record<InvoicePaymentStatus, string> = {
  [InvoicePaymentStatus.FullyPaid]: "#2e7d32",
  [InvoicePaymentStatus.PartiallyPaid]: "#eb6f10",
  [InvoicePaymentStatus.Overdue]: "#ed0202",
  [InvoicePaymentStatus.Unpaid]: "#575554",
};

export const PAYMENT_STATUS_TILES = [
  {
    key: InvoicePaymentStatus.FullyPaid,
    title: PAYMENT_STATUS_LABELS[InvoicePaymentStatus.FullyPaid],
    color: PAYMENT_STATUS_COLORS[InvoicePaymentStatus.FullyPaid],
  },
  {
    key: InvoicePaymentStatus.PartiallyPaid,
    title: PAYMENT_STATUS_LABELS[InvoicePaymentStatus.PartiallyPaid],
    color: PAYMENT_STATUS_COLORS[InvoicePaymentStatus.PartiallyPaid],
  },
  {
    key: InvoicePaymentStatus.Overdue,
    title: PAYMENT_STATUS_LABELS[InvoicePaymentStatus.Overdue],
    color: PAYMENT_STATUS_COLORS[InvoicePaymentStatus.Overdue],
  },
  {
    key: InvoicePaymentStatus.Unpaid,
    title: PAYMENT_STATUS_LABELS[InvoicePaymentStatus.Unpaid],
    color: PAYMENT_STATUS_COLORS[InvoicePaymentStatus.Unpaid],
  },
];
export const PAYMENT_TRANSACTION_TILES = [
  {
    key: TransactionType.Sale,
    title: "Payments",
    color: "#3285bd",
  },
  {
    key: TransactionType.Refund,
    title: "Refunds",
    color: "#ef4444",
  },
];
export const COUNTRY_CONFIG: Record<
  CountryCode,
  {
    stateLabel: string;
    zipLabel: string;
    statesDropdown: DropdownItem[];
  }
> = {
  US: {
    stateLabel: "State",
    zipLabel: "ZIP Code",
    statesDropdown: US_STATES,
  },
  CA: {
    stateLabel: "Province",
    zipLabel: "Postal Code",
    statesDropdown: CANADA_PROVINCES,
  },
};
export const INVOICE_STATUS_DONUT_CONFIG = [
  {
    key: InvoiceStatus.PaymentReceived,
    label: "Payment Received",
    color: "#3ca042",
  },
  {
    key: InvoiceStatus.SendToCustomer,
    label: "Sent to Customer",
    color: "#3971ae",
  },
  {
    key: InvoiceStatus.InProgress,
    label: "In Progress",
    color: "#de902b",
  },
  {
    key: InvoiceStatus.Pending,
    label: "Pending",
    color: "#d04e44",
  },
];
